import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Loader2, ShieldCheck, RefreshCw } from 'lucide-react';
import { matchingAPI } from '../../lib/api';

function getErrorMessage(err) {
  return err?.response?.data?.detail || err?.message || 'Request failed';
}

// The four tallies operationally relevant to this card: whether the chain
// is even discovering BR OS evidence (bros_evidence_stamped,
// bros_groups_proposed), and whether armed auto-confirm attempts are
// succeeding or being refused downstream (bros_auto_confirmed,
// bros_auto_confirm_failed). Full breakdown lives on the Match Funnel card;
// this is a scoped read of the same GET /matches/match-funnel endpoint
// (routes/matching.py) so an operator doesn't have to cross-reference two
// cards to tell "armed but nothing queued" apart from "armed and queued but
// refused at confirm time".
const FUNNEL_TALLY_KEYS = [
  { key: 'bros_evidence_stamped', label: 'Evidence stamped (1:1)' },
  { key: 'bros_groups_proposed', label: 'Groups proposed' },
  { key: 'bros_auto_confirm_queued', label: 'Queued for confirm' },
  { key: 'bros_auto_confirmed', label: 'Auto-confirmed' },
  { key: 'bros_auto_confirm_failed', label: 'Auto-confirm failed' },
];

export function BrosEvidenceTierPanel() {
  const [preview, setPreview] = useState(null);
  const [applyResult, setApplyResult] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [applyLoading, setApplyLoading] = useState(false);
  const [unambiguousApplyResult, setUnambiguousApplyResult] = useState(null);
  const [unambiguousApplyLoading, setUnambiguousApplyLoading] = useState(false);
  const [funnel, setFunnel] = useState(null);
  const [funnelLoading, setFunnelLoading] = useState(false);
  const [funnelNotFound, setFunnelNotFound] = useState(false);
  const [funnelError, setFunnelError] = useState(null);

  const runPreview = async () => {
    setPreviewLoading(true);
    setApplyResult(null);
    try {
      const res = await matchingAPI.previewBrosEvidenceTier();
      setPreview(res.data);
    } catch (err) {
      setPreview({ error: getErrorMessage(err) });
    } finally {
      setPreviewLoading(false);
    }
  };

  const runFunnel = async () => {
    setFunnelLoading(true);
    setFunnelError(null);
    setFunnelNotFound(false);
    try {
      const { data } = await matchingAPI.matchFunnel();
      setFunnel(data);
    } catch (err) {
      if (err?.response?.status === 404) {
        setFunnelNotFound(true);
        setFunnel(null);
      } else {
        setFunnelError(getErrorMessage(err));
      }
    } finally {
      setFunnelLoading(false);
    }
  };

  const runApply = async () => {
    if (!window.confirm(
      `Arm auto-confirm for ${preview?.would_auto_confirm_count || 0} Wise exact-transfer-identity ` +
      'match(es)? This is a one-time switch -- confirms then happen automatically on the next ' +
      'scheduled auto-matching run, through the normal confirm path. Not reversible from here ' +
      '(revert individual matches via Reconciliation if needed).'
    )) return;
    setApplyLoading(true);
    try {
      const res = await matchingAPI.applyBrosEvidenceTier('wise_exact');
      setApplyResult(res.data);
    } catch (err) {
      setApplyResult({ error: getErrorMessage(err) });
    } finally {
      setApplyLoading(false);
    }
  };

  const runUnambiguousApply = async () => {
    const count = preview?.unambiguous?.would_auto_confirm_count || 0;
    if (!window.confirm(
      `Arm auto-confirm for ${count} amount/counterparty/date unambiguous match(es) (no Wise ` +
      'transfer id)? Independent of the Wise exact-transfer-identity flag above. This is a ' +
      'one-time switch -- confirms then happen automatically on the next scheduled auto-matching ' +
      'run. Not reversible from here (revert individual matches via Reconciliation if needed).'
    )) return;
    setUnambiguousApplyLoading(true);
    try {
      const res = await matchingAPI.applyBrosEvidenceTier('unambiguous');
      setUnambiguousApplyResult(res.data);
    } catch (err) {
      setUnambiguousApplyResult({ error: getErrorMessage(err) });
    } finally {
      setUnambiguousApplyLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-muted-foreground" />
          <CardTitle className="text-base">BR OS Evidence Tier</CardTitle>
        </div>
        <p className="text-xs text-muted-foreground">
          Uses BR OS's own payment_match block as match evidence: a same-transfer,
          amount-exact, date-close Wise payment ranks top and can auto-confirm once armed;
          Remittance/manual_bulk_mark_paid evidence is a ranking boost only, never
          auto-confirm-eligible. Preview shows what would auto-confirm; Apply is a one-time
          arm, not a repeated action.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={runPreview} disabled={previewLoading} data-testid="bros-evidence-tier-preview">
            {previewLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
            Preview
          </Button>
          {preview && !preview.error && !preview.armed && preview.would_auto_confirm_count > 0 && (
            <Button
              variant="default"
              size="sm"
              onClick={runApply}
              disabled={applyLoading || !!applyResult}
              data-testid="bros-evidence-tier-apply"
            >
              {applyLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
              Arm Auto-Confirm ({preview.would_auto_confirm_count})
            </Button>
          )}
        </div>

        {preview && (
          <>
            {preview.error ? (
              <p className="text-xs text-red-600">{preview.error}</p>
            ) : (
              <div className="space-y-2" data-testid="bros-evidence-tier-preview-result">
                <div className="flex flex-wrap gap-2">
                  {preview.armed && (
                    <Badge className="bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400 text-[10px]">
                      Armed
                    </Badge>
                  )}
                  {preview.tallies && Object.entries(preview.tallies).map(([key, count]) => (
                    <Badge key={key} variant="outline" className="text-[10px]" data-testid={'bros-evidence-tally-' + key}>
                      {key}: {count}
                    </Badge>
                  ))}
                  {preview.truncated && (
                    <span className="text-amber-600 text-xs">Truncated at scan cap</span>
                  )}
                </div>
                {preview.would_auto_confirm && preview.would_auto_confirm.length > 0 && (
                  <ul className="text-xs space-y-1" data-testid="bros-evidence-would-auto-confirm-list">
                    {preview.would_auto_confirm.map((w) => (
                      <li key={w.payment_id} className="font-mono">
                        {w.payment_id} · {w.invoice_ids.join(', ')} · {w.currency} {w.amount} · {w.payer_name}
                      </li>
                    ))}
                  </ul>
                )}
                {preview.blocked && preview.blocked.length > 0 && (
                  <ul className="text-xs space-y-1" data-testid="bros-evidence-blocked-list">
                    {preview.blocked.map((b) => (
                      <li key={b.payment_id} className="font-mono text-amber-700 dark:text-amber-400">
                        {b.payment_id} · {(b.invoice_ids || []).join(', ')} · blocked: {b.reason}
                      </li>
                    ))}
                  </ul>
                )}
                {preview.amount_mismatched && preview.amount_mismatched.length > 0 && (
                  <ul className="text-xs space-y-1" data-testid="bros-evidence-amount-mismatched-list">
                    {preview.amount_mismatched.map((m) => (
                      <li key={m.payment_id} className="font-mono text-red-700 dark:text-red-400">
                        {m.payment_id} · {(m.invoice_ids || []).join(', ')} · siblings sum {m.computed_sum} {m.currency} vs claimed {m.claimed_amount} {m.currency} ({m.reason})
                      </li>
                    ))}
                  </ul>
                )}
                <p className="text-xs text-muted-foreground">{preview.message}</p>
              </div>
            )}
          </>
        )}

        {applyResult && (
          <div className={`p-3 rounded text-xs border ${applyResult.error ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`} data-testid="bros-evidence-tier-apply-result">
            {applyResult.error ? (
              <p className="text-red-600">{applyResult.error}</p>
            ) : (
              <div className="space-y-1">
                <p><strong>Armed:</strong> {applyResult.newly_armed ? 'yes (just now)' : 'already armed'}</p>
                <p className="text-muted-foreground">{applyResult.message}</p>
              </div>
            )}
          </div>
        )}

        {preview && !preview.error && preview.unambiguous && (
          <div className="pt-2 border-t space-y-2" data-testid="bros-unambiguous-tier-section">
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs text-muted-foreground">
                Second arm: exact amount + counterparty + close date + single candidate + no
                ambiguous sibling (Remittance/manual sources, no Wise transfer id) -- its own
                flag, armed independently of the tier above.
              </p>
              {!preview.unambiguous.armed && (
                <Button
                  variant="default"
                  size="sm"
                  onClick={runUnambiguousApply}
                  disabled={unambiguousApplyLoading || !!unambiguousApplyResult}
                  data-testid="bros-unambiguous-tier-apply"
                >
                  {unambiguousApplyLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
                  Arm Auto-Confirm ({preview.unambiguous.would_auto_confirm_count})
                </Button>
              )}
            </div>
            <div className="flex flex-wrap gap-2" data-testid="bros-unambiguous-tier-preview-result">
              {preview.unambiguous.armed && (
                <Badge className="bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400 text-[10px]">
                  Armed
                </Badge>
              )}
              {preview.unambiguous.tallies && Object.entries(preview.unambiguous.tallies).map(([key, count]) => (
                <Badge key={key} variant="outline" className="text-[10px]" data-testid={'bros-unambiguous-tally-' + key}>
                  {key}: {count}
                </Badge>
              ))}
            </div>
            {preview.unambiguous.blocked && preview.unambiguous.blocked.length > 0 && (
              <ul className="text-xs space-y-1" data-testid="bros-unambiguous-blocked-list">
                {preview.unambiguous.blocked.map((b) => (
                  <li key={b.payment_id} className="font-mono text-amber-700 dark:text-amber-400">
                    {b.payment_id} · {(b.invoice_ids || []).join(', ')} · blocked: {b.reason}
                  </li>
                ))}
              </ul>
            )}
            {unambiguousApplyResult && (
              <div className={`p-3 rounded text-xs border ${unambiguousApplyResult.error ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`} data-testid="bros-unambiguous-tier-apply-result">
                {unambiguousApplyResult.error ? (
                  <p className="text-red-600">{unambiguousApplyResult.error}</p>
                ) : (
                  <div className="space-y-1">
                    <p><strong>Armed:</strong> {unambiguousApplyResult.newly_armed ? 'yes (just now)' : 'already armed'}</p>
                    <p className="text-muted-foreground">{unambiguousApplyResult.message}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <div className="pt-2 border-t space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-xs text-muted-foreground">
              Auto-match chain funnel tallies for this tier (from the latest chain run) --
              whether evidence is being discovered and whether armed auto-confirms are
              succeeding, distinct from the Preview projection above.
            </p>
            <Button variant="outline" size="sm" onClick={runFunnel} disabled={funnelLoading} data-testid="bros-evidence-funnel-refresh">
              {funnelLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <RefreshCw className="h-3 w-3 mr-1" />}
              Refresh
            </Button>
          </div>

          {funnelError && <p className="text-xs text-red-600">{funnelError}</p>}

          {funnelNotFound && (
            <p className="text-xs text-muted-foreground" data-testid="bros-evidence-funnel-empty">
              No auto-match chain yet for this scope -- run auto-matching from the Reconciliation page first.
            </p>
          )}

          {funnel && (
            <div className="flex flex-wrap gap-2" data-testid="bros-evidence-funnel-result">
              {FUNNEL_TALLY_KEYS.map(({ key, label }) => (
                <Badge key={key} variant="outline" className="text-[10px]" data-testid={'bros-evidence-funnel-tally-' + key}>
                  {label}: {(funnel.stats || {})[key] ?? 0}
                </Badge>
              ))}
            </div>
          )}

          {/* Captured by services/auto_matching_chain.py's drain (_sample(samples[
              "bros_auto_confirm_failures"], ...)) and already served by GET
              /matches/match-funnel under stats.samples -- previously collected but
              never rendered anywhere, so a nonzero auto-confirm-failed tally above
              had no visible "why" (2026-07-23 WP-CC9D00F3 diagnosis, Phase 1). */}
          {funnel && (funnel.stats?.samples?.bros_auto_confirm_failures || []).length > 0 && (
            <div className="pt-1 space-y-1" data-testid="bros-evidence-funnel-failure-samples">
              <p className="text-xs text-muted-foreground">Auto-confirm failure reasons (most recent sample):</p>
              <ul className="text-xs space-y-1">
                {funnel.stats.samples.bros_auto_confirm_failures.map((f, i) => (
                  <li key={f.ref_id || i} className="font-mono text-red-700 dark:text-red-400">
                    {f.kind}:{f.ref_id} · {f.reason}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
